import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DownloadIcon from '@mui/icons-material/Download';
import { usersApi } from '../services/api';
import { calcVariance } from '../utils/dateUtils';
import TicketTable, { type Ticket } from './shared/TicketTable';
import { extractApiError } from '../types/apiError';
import { HubDateField } from './clients/hub/designSystem';

interface TicketItem {
  id: number;
  metric_id: number;
  metric_name: string;
  metric_unit: string | null;
  department_name: string | null;
  week_date: string;
  source_id: string;
  title: string;
  url: string | null;
  assignee: string | null;
  status: 'positive' | 'negative' | 'neutral';
  details: {
    client?: string;
    stage?: string;
    dueDate?: string;
    resolvedDate?: string;
    [key: string]: any;
  } | null;
}

function parseDate(raw: string | undefined | null): number {
  if (!raw) return 0;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

function exportToCsv(filename: string, headers: string[], rows: string[][]) {
  const escape = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function getDatePreset(preset: string): { start: string; end: string } {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  const today = fmt(now);
  switch (preset) {
    case 'this_week': {
      const d = new Date(now); d.setDate(d.getDate() - d.getDay());
      return { start: fmt(d), end: today };
    }
    case 'last_week': {
      const d = new Date(now); d.setDate(d.getDate() - d.getDay() - 7);
      const e = new Date(d); e.setDate(e.getDate() + 6);
      return { start: fmt(d), end: fmt(e) };
    }
    case 'last_2_weeks': {
      const d = new Date(now); d.setDate(d.getDate() - 14);
      return { start: fmt(d), end: today };
    }
    case 'this_month': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: fmt(d), end: today };
    }
    case 'last_month': {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: fmt(d), end: fmt(e) };
    }
    case 'this_quarter': {
      const qm = Math.floor(now.getMonth() / 3) * 3;
      const d = new Date(now.getFullYear(), qm, 1);
      return { start: fmt(d), end: today };
    }
    case 'last_quarter': {
      const qm = Math.floor(now.getMonth() / 3) * 3 - 3;
      const d = new Date(now.getFullYear(), qm, 1);
      const e = new Date(now.getFullYear(), qm + 3, 0);
      return { start: fmt(d), end: fmt(e) };
    }
    default:
      return { start: '', end: '' };
  }
}

type StatusFilter = 'all' | 'positive' | 'negative' | 'neutral';
type SortField = 'title' | 'client' | 'metric' | 'stage' | 'due' | 'resolved' | 'status' | 'variance' | 'week';
type SortDir = 'asc' | 'desc';

function mapItemToTicket(item: TicketItem, hasVariance: boolean): Ticket {
  const variance = hasVariance ? calcVariance(item.details?.dueDate, item.details?.resolvedDate) : null;
  return {
    id: item.id,
    title: item.title,
    url: item.url || undefined,
    status: item.status,
    client_name: item.details?.client,
    assigned_name: item.assignee || undefined,
    due_date: item.details?.dueDate,
    metric_name: item.metric_name,
    stage: item.details?.stage,
    resolved_date: item.details?.resolvedDate,
    week_date: item.week_date,
    variance: variance ?? undefined,
  };
}

export default function MyTicketsView() {
  const [items, setItems] = useState<TicketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unmapped, setUnmapped] = useState(false);
  const [assigneeName, setAssigneeName] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('week');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (statusFilter !== 'all') params.status = statusFilter;

      const res = await usersApi.getMyTickets(params);
      if (res.unmapped) {
        setUnmapped(true);
        setItems([]);
      } else {
        setUnmapped(false);
        setItems(res.items || []);
        setAssigneeName(res.assignee || '');
      }
    } catch (err: any) {
      setError(extractApiError(err, 'Failed to load tickets'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, statusFilter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSort = (field: string) => {
    const f = field as SortField;
    if (sortField === f) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(f);
      setSortDir(f === 'title' || f === 'client' || f === 'metric' || f === 'stage' ? 'asc' : 'desc');
    }
  };

  const uniqueStages = useMemo(() => {
    const stages = new Set<string>();
    items.forEach(item => {
      if (item.details?.stage) stages.add(item.details.stage);
    });
    return Array.from(stages).sort();
  }, [items]);

  const sortedItems = useMemo(() => {
    let arr = [...items];
    if (stageFilter !== 'all') {
      arr = arr.filter(item => (item.details?.stage || '') === stageFilter);
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'title':
          cmp = (a.title || '').localeCompare(b.title || '');
          break;
        case 'client':
          cmp = (a.details?.client || '').localeCompare(b.details?.client || '');
          break;
        case 'metric':
          cmp = (a.metric_name || '').localeCompare(b.metric_name || '');
          break;
        case 'stage':
          cmp = (a.details?.stage || '').localeCompare(b.details?.stage || '');
          break;
        case 'due':
          cmp = parseDate(a.details?.dueDate) - parseDate(b.details?.dueDate);
          break;
        case 'resolved':
          cmp = parseDate(a.details?.resolvedDate) - parseDate(b.details?.resolvedDate);
          break;
        case 'status': {
          const order: Record<string, number> = { positive: 0, neutral: 1, negative: 2 };
          cmp = (order[a.status] ?? 1) - (order[b.status] ?? 1);
          break;
        }
        case 'variance': {
          const va = calcVariance(a.details?.dueDate, a.details?.resolvedDate);
          const vb = calcVariance(b.details?.dueDate, b.details?.resolvedDate);
          cmp = (va?.days ?? -9999) - (vb?.days ?? -9999);
          break;
        }
        case 'week':
          cmp = parseDate(a.week_date) - parseDate(b.week_date);
          break;
      }
      return cmp * dir;
    });
    return arr;
  }, [items, sortField, sortDir, stageFilter]);

  const hasClient = items.some(i => i.details?.client);
  const hasStage = items.some(i => i.details?.stage);
  const hasDueDate = items.some(i => i.details?.dueDate);
  const hasResolvedDate = items.some(i => i.details?.resolvedDate);
  const hasVariance = hasDueDate && hasResolvedDate;

  const tickets: Ticket[] = useMemo(
    () => sortedItems.map(item => mapItemToTicket(item, hasVariance)),
    [sortedItems, hasVariance]
  );

  if (unmapped && !loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 2, textAlign: 'center' }}>
        <AssignmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>Your account hasn't been linked yet</Typography>
        <Typography variant="body2" color="text.disabled">Ask your admin to map your assignee identity in Settings &gt; Users &amp; Roles so your tickets appear here.</Typography>
      </Box>
    );
  }

  const positiveCount = items.filter(i => i.status === 'positive').length;
  const negativeCount = items.filter(i => i.status === 'negative').length;
  const neutralCount = items.filter(i => i.status === 'neutral').length;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Filter bar */}
      <Paper variant="outlined" sx={{ mb: 1, px: 2, py: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
        <ToggleButtonGroup
          size="small"
          value={statusFilter}
          exclusive
          onChange={(_e, val) => { if (val !== null) setStatusFilter(val); }}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', py: 0.4, px: 1.5 } }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="positive" sx={{ color: '#4caf50' }}>On Time</ToggleButton>
          <ToggleButton value="negative" sx={{ color: '#CF6679' }}>Late</ToggleButton>
          <ToggleButton value="neutral">Other</ToggleButton>
        </ToggleButtonGroup>
        {uniqueStages.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Stage</InputLabel>
            <Select
              value={stageFilter}
              label="Stage"
              onChange={(e) => setStageFilter(e.target.value)}
              sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.75 } }}
            >
              <MenuItem value="all"><em>All Stages</em></MenuItem>
              {uniqueStages.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: '0.8rem' }}>Quick range</InputLabel>
          <Select
            value=""
            label="Quick range"
            onChange={(e) => {
              const { start, end } = getDatePreset(e.target.value);
              setStartDate(start);
              setEndDate(end);
            }}
            sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.75 } }}
          >
            <MenuItem value="this_week">This Week</MenuItem>
            <MenuItem value="last_week">Last Week</MenuItem>
            <MenuItem value="last_2_weeks">Last 2 Weeks</MenuItem>
            <MenuItem value="this_month">This Month</MenuItem>
            <MenuItem value="last_month">Last Month</MenuItem>
            <MenuItem value="this_quarter">This Quarter</MenuItem>
            <MenuItem value="last_quarter">Last Quarter</MenuItem>
          </Select>
        </FormControl>
        <HubDateField
          label="From"
          value={startDate || null}
          onChange={(next) => setStartDate(next ?? '')}
          clearable
          minWidth={150}
        />
        <HubDateField
          label="To"
          value={endDate || null}
          onChange={(next) => setEndDate(next ?? '')}
          clearable
          minWidth={150}
        />
        {(startDate || endDate) && (
          <Button size="small" onClick={() => { setStartDate(''); setEndDate(''); }} sx={{ textTransform: 'none', fontSize: '0.72rem', minWidth: 0, px: 1 }}>
            Clear
          </Button>
        )}
        <Tooltip title="Export to CSV">
          <IconButton
            size="small"
            onClick={() => {
              const headers = ['Title', 'Client', 'Metric', 'Stage', 'Due Date', 'Resolved Date', 'Status', 'Variance', 'Week'];
              const rows = sortedItems.map(item => {
                const v = calcVariance(item.details?.dueDate, item.details?.resolvedDate);
                return [
                  item.title || '',
                  item.details?.client || '',
                  item.metric_name || '',
                  item.details?.stage || '',
                  item.details?.dueDate || '',
                  item.details?.resolvedDate || '',
                  item.status,
                  v?.label || '',
                  item.week_date || '',
                ];
              });
              exportToCsv(`my-tickets-${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Bulk selection action bar */}
      {selectedIds.size > 0 && (
        <Paper variant="outlined" sx={{ mb: 1, px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'rgba(201,169,97,0.08)', borderColor: 'primary.main', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
            {selectedIds.size} selected
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: '14px !important' }} />}
            onClick={() => {
              const selected = sortedItems.filter(i => selectedIds.has(i.id));
              const headers = ['Title', 'Client', 'Metric', 'Stage', 'Due Date', 'Resolved Date', 'Status', 'Week'];
              const rows = selected.map(item => [
                item.title || '', item.details?.client || '', item.metric_name || '',
                item.details?.stage || '', item.details?.dueDate || '',
                item.details?.resolvedDate || '', item.status, item.week_date || '',
              ]);
              exportToCsv(`selected-tickets-${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
            }}
            sx={{ textTransform: 'none', fontSize: '0.72rem' }}
          >
            Export selected
          </Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ textTransform: 'none', fontSize: '0.72rem', ml: 'auto' }}>
            Clear selection
          </Button>
        </Paper>
      )}

      {error && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!error && (
        <>
          {/* Summary bar */}
          <Box sx={{ px: 1, py: 1, display: 'flex', gap: 1.5, alignItems: 'center', mb: 1 }}>
            <Chip
              label={`${items.length} tickets`}
              size="small"
              sx={{ bgcolor: 'rgba(201,169,97,0.12)', color: 'primary.main', fontWeight: 600, fontSize: '0.75rem', height: 24 }}
            />
            {positiveCount > 0 && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label={`${positiveCount} on time`}
                size="small"
                sx={{ bgcolor: 'rgba(76,175,80,0.12)', color: '#4caf50', fontWeight: 500, fontSize: '0.72rem', height: 24, '& .MuiChip-icon': { color: '#4caf50' } }}
              />
            )}
            {negativeCount > 0 && (
              <Chip
                icon={<CancelIcon sx={{ fontSize: 14 }} />}
                label={`${negativeCount} late`}
                size="small"
                sx={{ bgcolor: 'rgba(207,102,121,0.12)', color: '#CF6679', fontWeight: 500, fontSize: '0.72rem', height: 24, '& .MuiChip-icon': { color: '#CF6679' } }}
              />
            )}
            {neutralCount > 0 && (
              <Chip
                label={`${neutralCount} other`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: 'text.secondary', fontWeight: 500, fontSize: '0.72rem', height: 24 }}
              />
            )}
            {assigneeName && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                Showing tickets for <strong>{assigneeName}</strong>
              </Typography>
            )}
          </Box>

          {/* Table */}
          <TicketTable
            tickets={tickets}
            loading={loading}
            statusFilter={statusFilter}
            onStatusFilterChange={(v) => setStatusFilter(v as StatusFilter)}
            statusOptions={['all', 'positive', 'negative', 'neutral']}
            statusLabels={{ all: 'All', positive: 'On Time', negative: 'Late', neutral: 'Other' }}
            hideStatusFilter
            showClientColumn={hasClient}
            showAssignedColumn={false}
            showPriorityColumn={false}
            showDueColumn={hasDueDate}
            showMetricColumn
            showStageColumn={hasStage}
            showResolvedColumn={hasResolvedDate}
            showVarianceColumn={hasVariance}
            showWeekColumn
            showIdColumn={false}
            showActionsColumn={false}
            showCheckboxColumn
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            sortableColumns={['title', 'client', 'metric', 'stage', 'due', 'resolved', 'status', 'variance', 'week']}
            emptyMessage="No tickets found"
          />
        </>
      )}
    </Box>
  );
}
